import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { COMPANY, LOGO_URL, NAV_LINKS } from "@/lib/constants";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goQuote = () => {
        setOpen(false);
        navigate("/inquiry?type=quote");
    };

    return (
        <header
            data-testid="site-navbar"
            className={`sticky top-0 z-50 glass-header transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(10,37,64,0.06)]" : ""}`}
        >
            <div className="container-lg flex items-center justify-between h-24 lg:h-28">
                <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
                    <img
                        src={LOGO_URL}
                        alt={COMPANY.name}
                        className="h-16 sm:h-20 lg:h-24 w-auto"
                        loading="eager"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.id}
                            to={l.to}
                            data-testid={`nav-link-${l.id}`}
                            className={({ isActive }) =>
                                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isActive && l.to === "/"
                                        ? "text-brand"
                                        : "text-navy/80 hover:text-brand hover:bg-brand-soft/60"
                                }`
                            }
                            end={l.to === "/"}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={`tel:${COMPANY.phoneRaw}`}
                        className="text-sm font-semibold text-navy hover:text-brand transition-colors"
                        data-testid="nav-phone"
                    >
                        {COMPANY.phone}
                    </a>
                    <button
                        onClick={goQuote}
                        data-testid="nav-cta-quote"
                        className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand transition-colors shadow-sm"
                    >
                        Request Quote
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                <button
                    className="lg:hidden p-2 text-navy"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="nav-menu-toggle"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl">
                    <div className="container-lg py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((l) => (
                            <Link
                                key={l.id}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-link-${l.id}`}
                                className="py-3 px-2 text-navy font-medium border-b border-slate-100"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <button
                            onClick={goQuote}
                            data-testid="nav-mobile-cta-quote"
                            className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-5 py-3 text-sm font-semibold hover:bg-brand-hover"
                        >
                            Request Quote <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
